import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { animated, config, useTransition } from '@react-spring/web';
import { ReactNode } from 'react';

import { IconX } from '../../common/icons/cdl/X';

import { CloseIconWrapper } from './components/CloseIconWrapper';
import { ModalBackdrop } from './components/ModalBackdrop';
import { ModalContent } from './components/ModalContent';
import { getModalWidth } from './util/getModalWidth';

const AnimatedPopupBackdrop = animated(ModalBackdrop);
const AnimatedPopupContent = animated(ModalContent);

export interface ModalProps {
    isOpen: boolean;
    children: ReactNode;
    onDismiss?: () => void;
    size?: 'small' | 'medium' | 'large';
    /**
     * @deprecated Use `size` instead
     */
    width?: number;
    scrollable?: boolean;
    dismissible?: boolean;
}

export const Modal = ({
    isOpen,
    onDismiss,
    size = 'medium',
    width,
    children,
    scrollable = true,
    dismissible = true,
}: ModalProps) => {
    const transitions = useTransition(isOpen, {
        from: { opacity: 0, y: -16, scale: 1 },
        enter: { opacity: 1, y: 0, scale: 1 },
        leave: { opacity: 0, y: 0, scale: 0.98 },
        config: {
            ...config.default,
            tension: 300,
            clamp: true,
        },
    });

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onDismiss?.();
        }
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
            <Dialog.Portal forceMount>
                {transitions((styles, show) => {
                    if (!show) {
                        return null;
                    }

                    return (
                        <div>
                            <AnimatedPopupBackdrop style={{ opacity: styles.opacity }}>
                                <AnimatedPopupContent
                                    style={styles}
                                    $width={width ? width : getModalWidth(size)}
                                    $scrollable={scrollable}
                                    onClick={(event) => event.stopPropagation()}
                                    onInteractOutside={(event) => {
                                        if (!dismissible) {
                                            event.preventDefault();
                                        }
                                    }}
                                    onEscapeKeyDown={(event: KeyboardEvent) => {
                                        if (!dismissible) {
                                            event.preventDefault();
                                        }
                                    }}
                                    onOpenAutoFocus={(event) => {
                                        event.preventDefault();
                                    }}
                                >
                                    {/* radix requires a title and description for accessibility */}
                                    <VisuallyHidden.Root asChild>
                                        <Dialog.Title>Modal</Dialog.Title>
                                    </VisuallyHidden.Root>
                                    <VisuallyHidden.Root asChild>
                                        <Dialog.Description>Modal content</Dialog.Description>
                                    </VisuallyHidden.Root>

                                    {dismissible ? (
                                        <CloseIconWrapper>
                                            <IconX width={16} height={16} />
                                        </CloseIconWrapper>
                                    ) : null}
                                    {children}
                                </AnimatedPopupContent>
                            </AnimatedPopupBackdrop>
                        </div>
                    );
                })}
            </Dialog.Portal>
        </Dialog.Root>
    );
};
